/**
 * AlgoGreeks Model Portfolio card — Phase B (Session 142e).
 * ----------------------------------------------------------
 *
 * Shows the running P&L of the "if you'd bought every pick equal-weight
 * on inception day" hypothetical portfolio.
 *
 * Data source: GET /api/alphascout/portfolio/model
 *   Returns inception snapshot + per-position breakdown + win rate.
 *
 * Visual hierarchy (left → right):
 *   1. Big headline — ₹1L → ₹X.XXL (±Y.YY%)
 *   2. Days since inception + open/closed counts
 *   3. Win rate gauge (colour shifts at 55% and 65%)
 *   4. Best / Worst position chips
 *
 * The card stays compact (h-24 on desktop, stacks on mobile) so it sits
 * above the PicksTable without pushing the table below the fold.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TrendingUp, TrendingDown, Trophy, AlertTriangle, Info, Activity,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';

/** Compact INR formatter — mirrors the backend _inr_compact helper so
 *  the UI stays in lockstep with the Telegram digest. */
const inrCompact = (n) => {
  if (n === null || n === undefined) return '–';
  const x = Number(n);
  if (!Number.isFinite(x)) return '–';
  if (Math.abs(x) >= 1e7) return `${(x / 1e7).toFixed(2)} Cr`;
  if (Math.abs(x) >= 1e5) return `${(x / 1e5).toFixed(2)}L`;
  return x.toLocaleString('en-IN');
};

const formatDate = (iso) => {
  if (!iso) return '–';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const daysSinceInception = (inceptionIso) => {
  if (!inceptionIso) return null;
  try {
    const start = new Date(inceptionIso + 'T00:00:00+05:30');
    const now = new Date();
    return Math.max(0, Math.floor((now - start) / 86400000));
  } catch { return null; }
};

const ModelPortfolioCard = () => {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [nav, setNav] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const [pr, nr] = await Promise.all([
          authFetch(`${API}/api/alphascout/portfolio/model`),
          authFetch(`${API}/api/alphascout/portfolio/nav-history?max_points=120`),
        ]);
        if (!pr.ok) throw new Error(`HTTP ${pr.status}`);
        const d = await pr.json();
        const n = nr.ok ? await nr.json() : null;
        if (!cancelled) {
          setData(d);
          setNav(n);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'failed to load');
      }
    };
    fetch();
    const iv = setInterval(fetch, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [authFetch]);

  if (error && !data) {
    return (
      <div
        className="bg-surface border border-border/40 rounded-sm px-4 py-3 text-xs text-muted-foreground flex items-center gap-2"
        data-testid="model-portfolio-card-error"
      >
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
        AlgoGreeks Model Portfolio unavailable — {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="bg-surface border border-border/40 rounded-sm px-4 py-3 animate-pulse text-xs text-muted-foreground"
        data-testid="model-portfolio-card-loading"
      >
        Loading AlgoGreeks Model Portfolio…
      </div>
    );
  }

  const inc = data.inception || {};
  const sum = data.summary || {};
  const pct = Number(inc.total_return_pct ?? 0);
  const gain = pct >= 0;
  const days = daysSinceInception(inc.inception_date);
  const winRate = Number(sum.win_rate_pct ?? 0);
  const winColor =
    winRate >= 65 ? 'text-emerald-400'
      : winRate >= 55 ? 'text-amber-300'
      : 'text-rose-400';

  return (
    <div
      className="bg-gradient-to-br from-surface to-surface/60 border border-border/60 rounded-sm px-4 py-3"
      data-testid="model-portfolio-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-amber-400" />
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
            AlgoGreeks Model Portfolio
          </h3>
        </div>
        <span className="text-[10px] text-muted-foreground font-mono" title="Inception date">
          Since {formatDate(inc.inception_date)}
          {days !== null && <span className="ml-1.5 opacity-75">• {days}d</span>}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 items-center">
        {/* Headline — capital → current */}
        <div data-testid="model-portfolio-headline">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
            ₹{inrCompact(inc.capital_invested || 100000)} invested →
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold font-mono text-foreground">
              ₹{inrCompact(inc.current_value)}
            </span>
            <span
              className={`text-sm font-bold font-mono ${gain ? 'text-emerald-400' : 'text-rose-400'} flex items-center gap-1`}
              data-testid="model-portfolio-return-pct"
            >
              {gain ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {gain ? '+' : ''}{pct.toFixed(2)}%
            </span>
          </div>
          {/* Session 142f — vs NIFTY 50 alpha line */}
          {data.benchmark && data.benchmark.benchmark_return_pct !== null && (
            <div
              className="flex items-center gap-1.5 mt-1 text-[10px] font-mono"
              data-testid="model-portfolio-alpha"
              title="Alpha = Model Portfolio return minus NIFTY 50 return since inception"
            >
              <span className="text-muted-foreground">
                vs {data.benchmark.benchmark_symbol}:
              </span>
              <span className="text-muted-foreground">
                {Number(data.benchmark.benchmark_return_pct) >= 0 ? '+' : ''}
                {Number(data.benchmark.benchmark_return_pct).toFixed(2)}%
              </span>
              {data.alpha_pct !== null && data.alpha_pct !== undefined && (
                <span
                  className={`ml-1 px-1.5 py-0.5 rounded-sm font-semibold ${
                    data.alpha_pct >= 0
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-rose-500/15 text-rose-400'
                  }`}
                >
                  {data.alpha_pct >= 0 ? '+' : ''}
                  {Number(data.alpha_pct).toFixed(2)}% α
                </span>
              )}
            </div>
          )}
        </div>

        {/* Position counts */}
        <div className="space-y-0.5" data-testid="model-portfolio-counts">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Positions
          </div>
          <div className="text-sm font-mono text-foreground">
            {sum.open_count ?? 0} open
            <span className="text-muted-foreground"> · {sum.closed_count ?? 0} closed</span>
          </div>
          {(sum.avg_hold_days ?? 0) > 0 && (
            <div className="text-[10px] text-muted-foreground font-mono">
              avg hold {sum.avg_hold_days}d
            </div>
          )}
        </div>

        {/* Win rate */}
        <div className="space-y-0.5" data-testid="model-portfolio-winrate">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Win Rate
          </div>
          <div className={`text-sm font-mono font-semibold ${winColor}`}>
            {sum.wins ?? 0} of {(sum.wins ?? 0) + (sum.losses ?? 0)} ({winRate.toFixed(0)}%)
          </div>
          <div className="text-[10px] text-muted-foreground font-mono">
            avg realized {Number(sum.avg_realized_pct ?? 0).toFixed(2)}%
          </div>
        </div>

        {/* Best / Worst chips */}
        <div className="space-y-1" data-testid="model-portfolio-bestworst">
          {data.best_position && (
            <div className="flex items-center gap-1.5 text-xs font-mono">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="text-muted-foreground">Best</span>
              <span className="text-foreground font-semibold">{data.best_position.symbol}</span>
              <span className="text-emerald-400">+{Number(data.best_position.return_pct).toFixed(2)}%</span>
            </div>
          )}
          {data.worst_position && (
            <div className="flex items-center gap-1.5 text-xs font-mono">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-400" />
              <span className="text-muted-foreground">Worst</span>
              <span className="text-foreground font-semibold">{data.worst_position.symbol}</span>
              <span className={Number(data.worst_position.return_pct) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {Number(data.worst_position.return_pct) >= 0 ? '+' : ''}
                {Number(data.worst_position.return_pct).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Session 142f — Portfolio vs NIFTY 50 performance chart. Rendered
          only when we have at least 2 points (first day = flat line). */}
      {nav && Array.isArray(nav.points) && nav.points.length >= 2 && (
        <div
          className="mt-3 pt-3 border-t border-border/40"
          data-testid="model-portfolio-chart"
        >
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Activity className="w-3 h-3" />
            Portfolio vs {nav.benchmark_symbol || 'NIFTY 50'} — {nav.points.length} days
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={nav.points}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="var(--border, #2a2a2a)"
                  strokeOpacity={0.4}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground, #888)' }}
                  tickFormatter={(d) => {
                    try {
                      return new Date(d).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short',
                      });
                    } catch { return d; }
                  }}
                  minTickGap={40}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground, #888)' }}
                  tickFormatter={(v) => `₹${inrCompact(v)}`}
                  domain={['auto', 'auto']}
                  width={62}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface, #18181b)',
                    border: '1px solid var(--border, #2a2a2a)',
                    borderRadius: '4px',
                    fontSize: '11px',
                  }}
                  formatter={(value, name) => [`₹${inrCompact(value)}`, name]}
                  labelFormatter={(d) => {
                    try {
                      return new Date(d).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      });
                    } catch { return d; }
                  }}
                />
                <Legend
                  verticalAlign="top"
                  height={20}
                  iconType="line"
                  wrapperStyle={{ fontSize: 10, paddingBottom: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="portfolio"
                  name="Model Portfolio"
                  stroke="#34d399"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="benchmark"
                  name={nav.benchmark_symbol || 'NIFTY 50'}
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-border/40 flex items-center gap-1 text-[10px] text-muted-foreground/80">
        <Info className="w-3 h-3" />
        <span>
          Equal-weight model assumes ₹1L deployed on inception day; proceeds redeploy on swaps. Ignores costs & slippage.
        </span>
      </div>
    </div>
  );
};

export default ModelPortfolioCard;
